import type { AssignableProjectRole, PrincipalOptions } from "@esi/shared";
import { useMutation } from "@tanstack/react-query";
import { ListFilter, Search, Users } from "lucide-react";
import { type RefObject, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Avatar } from "@/components/Avatar";
import { InlineError, Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSession } from "../../session.tsx";
import { useUnsavedWork } from "../../shell/session-ended.ts";
import { RolePicker } from "../RolePicker.tsx";
import { type AccessApi, type AccessKind, accessApi } from "./access-api.ts";
import { EMPTY_PERSON, PeoplePicker, type PersonField, personTarget } from "./PeoplePicker.tsx";
import { projectRefusal } from "./refusals.ts";

/** What each kind starts with: people usually contribute, groups and rules usually read. */
const DEFAULT_ROLE: Record<AccessKind, AssignableProjectRole> = {
  person: "contributor",
  group: "reader",
  rule: "reader",
};

/**
 * Adds a person, a directory group or an attribute rule with a role. Only the kinds the caller
 * may manage are offered; groups and rules already granted are left out, and a search narrows
 * the rest. People are found through the project's people search when the routes offer one
 * (`PeoplePicker`), else, and when nobody is found, added by e-mail. The server's refusal
 * stays in the dialog in plain words.
 */
export function AddAccessDialog(props: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  /** The routes to add through; the project's own when absent (governance passes its own). */
  access?: AccessApi;
  manageMembers: boolean;
  manageGrants: boolean;
  /** Groups and rules the caller may grant (`/api/me/principals`); absent while loading. */
  principals: PrincipalOptions | undefined;
  /** Why the groups and rules could not be listed, when they could not. */
  principalsError?: string | undefined;
  /** What already has a role, so it is not offered again. */
  granted: readonly { kind: AccessKind; id: string }[];
  /**
   * Whether the granted groups and rules are known. Until they are, none is offered: adding a
   * rule is an upsert, and offering one already granted would silently change its role.
   */
  grantsKnown: { group: boolean; rule: boolean };
  /** Why a granted list could not be loaded, per kind. */
  grantsError?: { group?: string | undefined; rule?: string | undefined };
  /** Refreshes what an addition (or its refusal) may have changed. */
  onAdded: () => void;
}) {
  const { t } = useTranslation();
  // A request in flight owns the dialog: Escape or an outside click must not drop its answer.
  // A ref, not state: only the close guard reads it.
  const busy = useRef(false);
  // An open people list takes the first Escape.
  const closeList = useRef<(() => boolean) | null>(null);
  return (
    <AppDialog
      open={props.open}
      onClose={() => {
        if (!busy.current) {
          props.onClose();
        }
      }}
      onEscapeKeyDown={(event) => {
        if (closeList.current?.()) {
          event.preventDefault();
        }
      }}
      title={t("settings.members.addTitle")}
      description={t("settings.members.addLead")}
    >
      <AddAccessForm {...props} busy={busy} closeList={closeList} />
    </AppDialog>
  );
}

function AddAccessForm(
  props: Parameters<typeof AddAccessDialog>[0] & {
    busy: RefObject<boolean>;
    closeList: RefObject<(() => boolean) | null>;
  },
) {
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken } = useSession();
  const kinds = [
    ...(props.manageMembers ? (["person"] as const) : []),
    ...(props.manageGrants ? (["group", "rule"] as const) : []),
  ];
  const [kind, setKind] = useState<AccessKind>(kinds[0] ?? "person");
  const [email, setEmail] = useState("");
  const [person, setPerson] = useState<PersonField>(EMPTY_PERSON);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState("");
  const [roles, setRoles] = useState(DEFAULT_ROLE);
  useUnsavedWork(email.trim() !== "" || person.text.trim() !== "" || picked !== "");
  const role = roles[kind];
  const access = props.access ?? accessApi(props.projectId, csrfToken);
  const people = access.people;
  const submit = useRef<HTMLButtonElement>(null);

  const options =
    kind === "person"
      ? []
      : (kind === "group"
          ? (props.principals?.groups.map((g) => ({ id: g.id, name: g.displayName })) ?? [])
          : (props.principals?.rules.map((r) => ({ id: r.id, name: r.name })) ?? [])
        ).filter((o) => !props.granted.some((g) => g.kind === kind && g.id === o.id));
  const needle = query.trim().toLocaleLowerCase();
  const matches = options.filter((o) => o.name.toLocaleLowerCase().includes(needle));
  const chosen = matches.some((o) => o.id === picked) ? picked : "";
  const target = people ? personTarget(person) : null;
  const ready =
    kind === "person" ? (people ? target !== null : email.trim() !== "") : chosen !== "";

  // The kind travels with the request: what was submitted, whatever the tab shows now. A
  // picked person goes by user ID, a typed address by e-mail.
  const add = useMutation({
    mutationFn: (input: {
      kind: AccessKind;
      target: string;
      byId?: boolean;
      role: AssignableProjectRole;
    }) =>
      input.byId && people
        ? people.add(input.target, input.role)
        : access.add[input.kind](input.target, input.role),
    onSuccess: () => {
      // Cleared first, so closing is not held back as unsaved work.
      setEmail("");
      setPerson(EMPTY_PERSON);
      setPicked("");
      props.busy.current = false;
      props.onClose();
    },
    onSettled: () => props.onAdded(),
  });
  // Locked while a request is out: it owns the dialog until it settles.
  const locked = add.isPending;
  // An edit clears a shown refusal; it never resets a request still in flight.
  const edit = () => {
    if (!locked) {
      add.reset();
    }
  };
  const { busy } = props;
  useEffect(() => {
    busy.current = locked;
  }, [busy, locked]);

  const field =
    kind === "person" && people ? (
      <PeoplePicker
        value={person}
        onChange={(next) => {
          edit();
          setPerson(next);
        }}
        search={people.search}
        scope={props.projectId}
        closeList={props.closeList}
        onPicked={() => requestAnimationFrame(() => submit.current?.focus())}
        disabled={locked}
      />
    ) : kind === "person" ? (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-email`}>{t("settings.members.email")}</Label>
        <Input
          id={`${id}-email`}
          type="email"
          required
          autoComplete="off"
          aria-describedby={`${id}-email-hint`}
          value={email}
          onChange={(e) => {
            edit();
            setEmail(e.currentTarget.value);
          }}
        />
        <p id={`${id}-email-hint`} className="text-xs text-fg-muted">
          {t("settings.members.emailHint")}
        </p>
      </div>
    ) : (
      <div className="flex flex-col gap-2">
        {kind === "rule" ? <p className="text-xs text-fg-muted">{t("members.rulesHint")}</p> : null}
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-muted"
            strokeWidth={1.75}
          />
          <Input
            type="search"
            aria-label={t(`settings.members.search.${kind}`)}
            placeholder={t(`settings.members.search.${kind}`)}
            autoComplete="off"
            className="pl-9"
            value={query}
            onChange={(e) => {
              edit();
              setQuery(e.currentTarget.value);
            }}
          />
        </div>
        {props.principalsError || props.grantsError?.[kind] ? (
          <InlineError>{props.principalsError ?? props.grantsError?.[kind]}</InlineError>
        ) : !props.principals || !props.grantsKnown[kind] ? (
          <Skeleton className="h-24 w-full rounded-lg" />
        ) : matches.length === 0 ? (
          <p className="rounded-lg border border-line px-3 py-4 text-sm text-fg-secondary">
            {options.length === 0
              ? t(`settings.members.noneLeft.${kind}`)
              : t(`settings.members.noMatch.${kind}`)}
          </p>
        ) : (
          <RadioGroup
            aria-label={t(`settings.members.pick.${kind}`)}
            value={chosen}
            onValueChange={(next) => {
              edit();
              setPicked(next);
            }}
            className="max-h-60 gap-0 divide-y divide-line overflow-y-auto rounded-lg border border-line bg-surface-raised"
          >
            {matches.map((o) => (
              <label
                key={o.id}
                htmlFor={`${id}-${o.id}`}
                className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm text-fg transition-colors duration-(--duration-fast) hover:bg-surface-hover has-data-[state=checked]:bg-surface-selected"
              >
                <RadioGroupItem id={`${id}-${o.id}`} value={o.id} />
                <Avatar icon={kind === "group" ? Users : ListFilter} />
                <span className="min-w-0 truncate">{o.name}</span>
              </label>
            ))}
          </RadioGroup>
        )}
      </div>
    );

  return (
    <form
      aria-label={t("settings.members.addTitle")}
      className="flex min-w-0 flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready || add.isPending) {
          return;
        }
        if (kind !== "person") {
          add.mutate({ kind, target: chosen, role });
        } else if (target && "userId" in target) {
          add.mutate({ kind, target: target.userId, byId: true, role });
        } else {
          add.mutate({ kind, target: target?.email ?? email.trim(), role });
        }
      }}
    >
      {kinds.length > 1 ? (
        <Tabs
          value={kind}
          onValueChange={(next) => {
            if (!locked) {
              add.reset();
              setKind(next as AccessKind);
              // Each tab searches its own list.
              setQuery("");
              setPicked("");
            }
          }}
        >
          <TabsList
            variant="line"
            aria-label={t("settings.members.addKind")}
            className="w-full justify-start gap-4 border-b border-line px-0"
          >
            {kinds.map((k) => (
              <TabsTrigger key={k} value={k} className="flex-none px-0">
                {t(`settings.members.kinds.${k}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          {kinds.map((k) => (
            <TabsContent key={k} value={k} className="pt-2">
              {field}
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        field
      )}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-role`}>{t("settings.members.role")}</Label>
        <RolePicker
          id={`${id}-role`}
          value={role}
          className="w-full"
          onChange={(next) => {
            edit();
            setRoles((current) => ({ ...current, [kind]: next }));
          }}
        />
      </div>

      {add.isError ? (
        <Notice tone="danger">
          {projectRefusal(add.error, kind === "person" ? "member" : undefined)}
        </Notice>
      ) : null}
      <DialogFooter>
        <Button type="button" variant="outline" disabled={add.isPending} onClick={props.onClose}>
          {t("settings.cancel")}
        </Button>
        <Button ref={submit} type="submit" disabled={add.isPending || !ready}>
          {add.isPending ? t("settings.members.adding") : t("settings.members.addSubmit")}
        </Button>
      </DialogFooter>
    </form>
  );
}
